/* global Stripe */
import { OlsConfigStore } from '@wsb/guac-widget-shared/lib/common/ols-core/core-bundle';
import { AppDispatcher } from '@wsb/guac-widget-shared/lib/common/ols-core/shared-bundle';
import { CartApi } from '@wsb/guac-widget-shared/lib/common/ols-core/shop-bundle';
import * as PaymentRequestUtils from './PaymentRequestApi';
import * as PaymentIntentUtils from './PaymentIntentApi';
import DigitalWalletStore, { DigitalWalletConstants } from './DigitalWalletStore';
import { getOrderCompleteUrl } from './getOrderCompleteUrl';
import * as Metrics from '../../components/utils/trackMetric';
import * as MetricEvents from '../../../constants/metrics';

const { dispatchServerAction, dispatchViewAction } = AppDispatcher;

function createOrder(variant, callback, errorCallback) {
  const options = variant.require_phone_number ? { order: { require_phone_number: true } } : null;
  CartApi.createCart(options)
    .then(data => {
      addItemToOrder(data, variant).then(callback).catch(errorCallback);
    })
    .catch(errorCallback);
}

function addItemToOrder(order, variant) {
  const { id, quantity, options } = variant;
  return CartApi.addToOrder(order, id, quantity, options);
}

class PaymentRequestActions {
  init() {
    if (DigitalWalletStore.getState().stripeWasLoaded) {
      return;
    }

    const script = window.document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.type = 'text/javascript';
    script.onload = () => {
      dispatchServerAction({
        type: DigitalWalletConstants.STRIPE_WAS_LOADED
      });
    };
    window.document.body.appendChild(script);
  }

  checkAvailability() {
    const { stripe_config, country_iso, currency } = OlsConfigStore.getState();
    const { stripeWasLoaded } = DigitalWalletStore.getState();

    dispatchServerAction({
      type: DigitalWalletConstants.AVAILABILITY_WAS_CHECKED
    });

    if (stripeWasLoaded && typeof Stripe !== 'undefined') {
      this.stripe = Stripe(stripe_config.publishable_key, {
        stripeAccount: stripe_config.reference_id
      });
      const paymentRequest = this.stripe.paymentRequest({
        country: country_iso,
        currency: currency.code.toLowerCase(),
        total: { label: ' ', amount: 1 }
      });

      // Check the availability of the Payment Request API first.
      paymentRequest.canMakePayment().then(result => {
        if (result) {
          dispatchServerAction({
            type: DigitalWalletConstants.PAYMENT_REQUEST_IS_AVAILABLE,
            data: result
          });
        }
      });
    }
  }

  startSession(params) {
    const { styles, getOrderData, btnSelector, initPage, phoneRequired, isGalleryDetail } = params;
    const { stripe_config, country_iso, currency } = OlsConfigStore.getState();

    const paymentRequest = this.stripe.paymentRequest({
      country: country_iso,
      currency: currency.code.toLowerCase(),
      total: { label: ' ', amount: 1 },
      requestShipping: true,
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: phoneRequired
    });
    const prButton = this.stripe.elements().create('paymentRequestButton', {
      paymentRequest,
      style: styles
    });

    prButton.on('click', e => {
      const orderData = getOrderData();

      this.order = orderData.order;
      this.variant = orderData.variant;

      if (this.order) {
        paymentRequest.update(this.buildPaymentrequestOptions());
      } else if (this.variant) {
        if (this.variant.canCheckout) {
          const { price, quantity } = this.variant.values;
          paymentRequest.update({
            total: {
              label: OlsConfigStore.getState().store_name,
              amount: Math.round(price * quantity * 100)
            },
            displayItems: [],
            shippingOptions: []
          });
        } else {
          e.preventDefault();
          if (!this.variant.triedCheckout) {
            dispatchServerAction({
              type: DigitalWalletConstants.PAYMENT_REQUEST_BUTTON_CLICKED
            });
          }
          return;
        }
      }

      dispatchViewAction({
        type: DigitalWalletConstants.SESSION_STARTED
      });

      paymentRequest.canMakePayment().then(result => {
        if (result) {
          if (initPage === 'product_detail') {
            if (isGalleryDetail && !result.applePay) {
              Metrics.track(MetricEvents.PRODUCT_LIST_PAYMENT_REQUEST_BUTTON_CLICK);
            } else {
              result.applePay
                ? Metrics.track(MetricEvents.PDP_APPLE_PAY_BUTTON_CLICK)
                : Metrics.track(MetricEvents.PDP_PAYMENT_REQUEST_BUTTON_CLICK);
            }
          } else {
            result.applePay
              ? Metrics.track(MetricEvents.CART_APPLE_PAY_BUTTON_CLICK)
              : Metrics.track(MetricEvents.CART_PAYMENT_REQUEST_BUTTON_CLICK);
          }
        }
      });
    });

    paymentRequest.canMakePayment().then(result => {
      if (result) {
        prButton.mount(btnSelector);

        // Due to multiple callbacks to this, we want to throttle the impressions
        // to make sure we don't overcount
        if (window._isTrackingPrButtonImpression) return;
        window._isTrackingPrButtonImpression = true;
        window.setTimeout(() => (window._isTrackingPrButtonImpression = false), 2e3);

        if (initPage === 'product_detail') {
          if (isGalleryDetail && !result.applePay) {
            Metrics.track(MetricEvents.PRODUCT_LIST_PAYMENT_REQUEST_BUTTON_IMPRESSION);
          } else {
            result.applePay
              ? Metrics.track(MetricEvents.PDP_APPLE_PAY_BUTTON_IMPRESSION)
              : Metrics.track(MetricEvents.PDP_PAYMENT_REQUEST_BUTTON_IMPRESSION);
          }
        } else {
          result.applePay
            ? Metrics.track(MetricEvents.CART_APPLE_PAY_BUTTON_IMPRESSION)
            : Metrics.track(MetricEvents.CART_PAYMENT_REQUEST_BUTTON_IMPRESSION);
        }
      }
    });

    paymentRequest.on('cancel', () => {
      dispatchViewAction({
        type: DigitalWalletConstants.SESSION_CANCELED
      });
    });

    paymentRequest.on('shippingaddresschange', e => {
      const updateShipAddress = () => {
        PaymentIntentUtils.updateCartShipAddress(this.order, e.shippingAddress)
          .then(order => {
            this.order = order;
            e.updateWith({
              status: 'success',
              total: this.buildTotal(),
              displayItems: this.buildLineItems(),
              shippingOptions: this.buildShippingMethods()
            });
          })
          .catch(() => e.updateWith({ status: 'fail' }));
      };

      if (this.order) {
        updateShipAddress();
      } else if (this.variant) {
        const successCallback = order => {
          this.order = order;
          updateShipAddress();
        };
        const errorCallback = () => {
          e.updateWith({ status: 'fail' });
        };

        createOrder(this.variant.values, successCallback, errorCallback);
      }
    });

    paymentRequest.on('shippingoptionchange', e => {
      PaymentIntentUtils.updateCartShipMethod(this.order, e.shippingOption)
        .then(order => {
          this.order = order;
          e.updateWith({
            status: 'success',
            total: this.buildTotal(),
            displayItems: this.buildLineItems()
          });
        })
        .catch(() => e.updateWith({ status: 'fail' }));
    });

    if (stripe_config.payment_intent_on_pdp_and_cart) {
      this.handlePaymentIntent(paymentRequest, initPage);
    } else {
      this.handlePaymentRequest(paymentRequest, initPage);
    }

    return prButton;
  }

  handlePaymentRequest(paymentRequest, initPage) {
    const { timeout } = DigitalWalletStore.getState();

    paymentRequest.on('token', e => {
      PaymentRequestUtils.completeOrder(this.order, e, initPage)
        .then(order => {
          e.complete('success');
          setTimeout(() => {
            window.location.href = getOrderCompleteUrl(order);
          }, timeout || 1000);
        })
        .catch(() => e.complete('fail'));
    });
  }

  handlePaymentIntent(paymentRequest, initPage) {
    const { timeout } = DigitalWalletStore.getState();

    paymentRequest.on('paymentmethod', e => {
      PaymentIntentUtils.processPaymentIntent(this.order, e, initPage)
        .then(order => {
          if (order.state === 'complete') {
            e.complete('success');
            setTimeout(() => {
              window.location.href = getOrderCompleteUrl(order);
            }, timeout || 1000);
          } else if (order.stripe_checkout_info) {
            // Stripe requires SCA flow
            e.complete('success');
            this.confirmPaymentIntent(order);
          }
        })
        .catch(error => {
          e.complete('fail');
          dispatchViewAction({
            type: DigitalWalletConstants.SESSION_FAILED,
            error: { messages: { general_error: error.response.error } }
          });
        });
    });
  }

  confirmPaymentIntent(order) {
    const { stripe_checkout_info } = order;
    const { payment_intent_client_secret } = stripe_checkout_info;
    const { timeout } = DigitalWalletStore.getState();

    this.stripe.handleCardAction(payment_intent_client_secret).then(res => {
      if (res.error) {
        dispatchViewAction({
          type: DigitalWalletConstants.SESSION_FAILED,
          error: { messages: { general_error: res.error.message } }
        });
      } else {
        PaymentIntentUtils.confirmPaymentIntent(order, res.paymentIntent, stripe_checkout_info)
          .then(completedOrder => {
            setTimeout(() => {
              window.location.href = getOrderCompleteUrl(completedOrder);
            }, timeout || 1000);
          })
          .catch(error => {
            dispatchViewAction({
              type: DigitalWalletConstants.SESSION_FAILED,
              error: { messages: { general_error: error.response.error } }
            });
          });
      }
    });
  }

  buildPaymentrequestOptions() {
    return {
      total: this.buildTotal(),
      displayItems: this.buildLineItems(),
      shippingOptions: this.buildShippingMethods()
    };
  }

  buildTotal() {
    return {
      label: OlsConfigStore.getState().store_name,
      amount: Math.round(this.order.balance_due * 100)
    };
  }

  buildLineItems() {
    const lineItems = [];

    if (this.order && this.order.summary_line_items) {
      for (const item of this.order.summary_line_items) {
        lineItems.push({
          label: item.label,
          amount: Math.round(item.amount * 100)
        });
      }
    }

    return lineItems;
  }

  buildShippingMethods() {
    const methods = [];

    if (this.order) {
      for (const rate of this.order.shipping_rates) {
        methods.push({
          id: rate.id.toString(),
          label: rate.method,
          detail: '',
          amount: Math.round(rate.cost * 100)
        });
      }
    }

    return methods;
  }
}

export default new PaymentRequestActions();
